import { useCallback, useEffect, useState } from 'react'
import type { User } from '@shared/types'
import { get, patch, post } from '../../lib/api'
import { useToast } from '../../state/toast'
import { Button, ErrorNote, Field, SectionHeading, Sheet, Spinner } from '../../components/ui'

export default function AdminPeople() {
  const toast = useToast()
  const [users, setUsers] = useState<User[] | null>(null)
  const [editing, setEditing] = useState<Partial<User> | null>(null)
  const [handover, setHandover] = useState<{ name: string; email: string; password: string } | null>(null)

  const load = useCallback(async () => {
    setUsers((await get<{ users: User[] }>('/api/admin/users')).users)
  }, [])

  useEffect(() => {
    void load().catch(() => setUsers([]))
  }, [load])

  if (!users) return <div className="py-16"><Spinner /></div>

  const resetPassword = async (user: User) => {
    try {
      const { tempPassword } = await post<{ tempPassword: string }>(
        `/api/admin/users/${user.id}/reset-password`,
      )
      setHandover({ name: user.name, email: user.email, password: tempPassword })
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not reset the password')
    }
  }

  const setActive = async (user: User, active: boolean) => {
    try {
      await patch(`/api/admin/users/${user.id}`, { active })
      toast.success(active ? `${user.name} reactivated` : `${user.name} deactivated`)
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update')
    }
  }

  return (
    <div className="space-y-4">
      <SectionHeading
        action={
          <Button className="min-h-9 px-3 text-xs" onClick={() => setEditing({ role: 'worker' })}>
            Add crew
          </Button>
        }
      >
        {users.filter((u) => u.active).length} active
      </SectionHeading>

      <div className="space-y-2">
        {users.map((user) => (
          <div key={user.id} className={`card p-3 ${user.active ? '' : 'opacity-55'}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-bold">
                  {user.name}
                  {user.role === 'admin' && (
                    <span className="chip ml-2 bg-ink text-brand">Office</span>
                  )}
                  {user.mustChangePassword && (
                    <span className="chip ml-2 bg-ink text-brand">Temp password</span>
                  )}
                </p>
                <p className="truncate text-xs text-graphite-2">{user.email}</p>
                {user.phone && <p className="text-xs text-graphite-2">{user.phone}</p>}
              </div>
              <Button className="min-h-9 shrink-0 px-3 text-xs" onClick={() => setEditing(user)}>
                Edit
              </Button>
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void resetPassword(user)}
                className="link text-xs"
              >
                Reset password
              </button>
              <button
                type="button"
                onClick={() => void setActive(user, !user.active)}
                className="text-xs font-bold text-alarm underline underline-offset-2"
              >
                {user.active ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <PersonSheet
          person={editing}
          onClose={() => setEditing(null)}
          onCreated={(details) => {
            setEditing(null)
            setHandover(details)
            void load()
          }}
          onSaved={async () => {
            setEditing(null)
            toast.success('Saved')
            await load()
          }}
        />
      )}

      {handover && (
        <Sheet
          open
          onClose={() => setHandover(null)}
          title="Temporary password"
          footer={
            <Button variant="primary" size="lg" className="w-full" onClick={() => setHandover(null)}>
              Done
            </Button>
          }
        >
          <p className="text-sm">
            Give this to <strong>{handover.name}</strong>. They sign in with{' '}
            <strong>{handover.email}</strong> and choose their own password straight away.
          </p>
          <p className="my-4 border border-line-strong bg-well px-4 py-3 text-center font-mono text-xl font-semibold tracking-wider select-all">
            {handover.password}
          </p>
          <p className="text-xs text-graphite-2">
            This is the only time it is shown. If it gets lost, reset the password again.
          </p>
        </Sheet>
      )}
    </div>
  )
}

function PersonSheet({
  person, onClose, onCreated, onSaved,
}: {
  person: Partial<User>
  onClose: () => void
  onCreated: (details: { name: string; email: string; password: string }) => void
  onSaved: () => Promise<void>
}) {
  const [draft, setDraft] = useState<Partial<User>>(person)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const isNew = !person.id

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      if (isNew) {
        const res = await post<{ user: User; tempPassword: string }>('/api/admin/users', {
          name: draft.name,
          email: draft.email,
          phone: draft.phone,
          role: draft.role,
        })
        onCreated({ name: res.user.name, email: res.user.email, password: res.tempPassword })
      } else {
        await patch(`/api/admin/users/${person.id}`, {
          name: draft.name,
          email: draft.email,
          phone: draft.phone,
          role: draft.role,
        })
        await onSaved()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save')
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={isNew ? 'Add crew member' : draft.name ?? 'Edit'}
      footer={
        <Button variant="primary" size="lg" className="w-full" loading={busy} onClick={() => void save()}>
          {isNew ? 'Create account' : 'Save'}
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label="Name" required>
          <input
            className="field"
            value={draft.name ?? ''}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </Field>
        <Field label="Email" required hint="This is how they sign in.">
          <input
            className="field"
            type="email"
            autoCapitalize="none"
            value={draft.email ?? ''}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          />
        </Field>
        <Field label="Phone">
          <input
            className="field"
            type="tel"
            value={draft.phone ?? ''}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
          />
        </Field>
        <Field label="Access" hint="Office access can see everyone's updates and change settings.">
          <select
            className="field"
            value={draft.role ?? 'worker'}
            onChange={(e) => setDraft({ ...draft, role: e.target.value as User['role'] })}
          >
            <option value="worker">Crew</option>
            <option value="admin">Office</option>
          </select>
        </Field>

        {isNew && (
          <p className="text-xs text-graphite-2">
            A temporary password is generated for you to hand over. New crew are signed up for every
            active reminder — trim that on the Reminders tab.
          </p>
        )}

        <ErrorNote>{error}</ErrorNote>
      </div>
    </Sheet>
  )
}
